import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  SafeAreaView,
  StatusBar,
  Modal,
  TextInput,
  Alert,
  Dimensions,
  Image,
  Animated,
  RefreshControl,
  ActivityIndicator,
} from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { MaterialCommunityIcons, Ionicons } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';

// ===== API Configuration =====
const API_BASE_URL = 'https://api.jobeng.ai';

const getCompanyId = async () => {
  try {
    const userData = await AsyncStorage.getItem('currentUser');
    if (userData) {
      const user = JSON.parse(userData);
      return user.company_id || user.companyId || null;
    }
    const companyProfile = await AsyncStorage.getItem('companyProfile');
    if (companyProfile) {
      const company = JSON.parse(companyProfile);
      return company.id || company.company_id || null;
    }
    return null;
  } catch (e) {
    console.error('Error getting company_id:', e);
    return null;
  }
};

const apiRequest = async (endpoint, options = {}) => {
  try {
    const companyId = await getCompanyId();
    // Split endpoint and query string
    const [path, queryString] = endpoint.split('?');
    // Route: auth/* goes to /api/auth/*, everything else to /api/data/{table}
    let baseUrl;
    if (path.startsWith('auth/')) {
      baseUrl = `${API_BASE_URL}/api/${path}`;
    } else {
      // Convert hyphens to underscores for DB table mapping
      const dbPath = path.replace(/-/g, '_');
      baseUrl = `${API_BASE_URL}/api/data/${dbPath}`;
    }
    const params = new URLSearchParams(queryString || '');
    if (companyId) params.set('company_id', companyId);
    const separator = params.toString() ? '?' : '';
    const url = `${baseUrl}${separator}${params.toString()}`;

    const response = await fetch(url, {
      headers: { 'Content-Type': 'application/json' },
      ...options,
      ...(options.body ? { body: JSON.stringify(options.body) } : {}),
    });

    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    console.error(`API request failed for ${endpoint}:`, error);
    throw error;
  }
};
// ===== End API Configuration =====


const { width, height } = Dimensions.get('window');

export default function NegotiationScreen({ navigation, route }) {
  const { candidate } = route.params;
  const [activeTab, setActiveTab] = useState('overview');
  const [isLoading, setIsLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [showCounterOfferModal, setShowCounterOfferModal] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);

  // Animation values
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const slideAnim = useRef(new Animated.Value(50)).current;

  // Negotiation state
  const [negotiationData, setNegotiationData] = useState({
    currentRound: 3,
    status: 'active',
    startDate: '2024-01-15',
    lastActivity: new Date().toISOString(),
    totalOffers: 4,
    acceptanceProbability: 78,
    negotiationScore: 85,
    timeToDecision: 5, // days
    originalOffer: {
      baseSalary: 75000,
      bonus: 15000,
      stockOptions: 25000,
      benefits: ['Health Insurance', 'Pension', 'Flexible Working'],
      startDate: '2024-03-01',
      vacationDays: 25,
      workLocation: 'Hybrid',
    },
    currentOffer: {
      baseSalary: 82000,
      bonus: 18000,
      stockOptions: 30000,
      benefits: ['Health Insurance', 'Pension', 'Flexible Working', 'Gym Membership'],
      startDate: '2024-02-15',
      vacationDays: 28,
      workLocation: 'Hybrid',
    },
    candidateRequests: {
      baseSalary: 85000,
      bonus: 20000,
      stockOptions: 35000,
      benefits: ['Health Insurance', 'Pension', 'Flexible Working', 'Gym Membership', 'Learning Budget'],
      startDate: '2024-02-01',
      vacationDays: 30,
      workLocation: 'Remote',
    },
  });

  const [counterOffer, setCounterOffer] = useState({
    baseSalary: negotiationData.currentOffer.baseSalary,
    bonus: negotiationData.currentOffer.bonus,
    stockOptions: negotiationData.currentOffer.stockOptions,
    vacationDays: negotiationData.currentOffer.vacationDays,
    startDate: negotiationData.currentOffer.startDate,
    workLocation: negotiationData.currentOffer.workLocation,
    additionalBenefits: [],
    message: '',
  });

  const [messageData, setMessageData] = useState({
    type: 'email',
    subject: '',
    message: '',
    priority: 'normal',
  });

  // Tab configuration
  const tabs = [
    { id: 'overview', name: 'Overview', icon: 'view-dashboard' },
    { id: 'comparison', name: 'Comparison', icon: 'compare' },
    { id: 'analytics', name: 'Analytics', icon: 'chart-line' },
    { id: 'history', name: 'History', icon: 'history' },
    { id: 'strategy', name: 'Strategy', icon: 'strategy' },
    { id: 'communication', name: 'Messages', icon: 'message-text' },
  ];

  // Negotiation history
  const [negotiationHistory] = useState([
    {
      id: 1,
      date: '2024-01-15',
      type: 'initial_offer',
      actor: 'company',
      title: 'Initial Offer Sent',
      details: 'Base salary: £75,000, Bonus: £15,000, Stock: £25,000',
      status: 'sent',
    },
    {
      id: 2,
      date: '2024-01-18',
      type: 'counter_offer',
      actor: 'candidate',
      title: 'Counter Offer Received',
      details: 'Requested: £85,000 base, £20,000 bonus, Remote work',
      status: 'received',
    },
    {
      id: 3,
      date: '2024-01-22',
      type: 'revised_offer',
      actor: 'company',
      title: 'Revised Offer Sent',
      details: 'Base salary: £80,000, Bonus: £17,000, Hybrid work',
      status: 'sent',
    },
    {
      id: 4,
      date: '2024-01-25',
      type: 'negotiation',
      actor: 'candidate',
      title: 'Negotiation Response',
      details: 'Accepted salary, requested additional vacation days',
      status: 'received',
    },
    {
      id: 5,
      date: '2024-01-28',
      type: 'current_offer',
      actor: 'company',
      title: 'Current Offer',
      details: 'Base salary: £82,000, Bonus: £18,000, 28 vacation days',
      status: 'active',
    },
  ]);

  // Available benefits
  const availableBenefits = [
    { id: 'health', name: 'Health Insurance', cost: 2400, included: true },
    { id: 'dental', name: 'Dental Insurance', cost: 600, included: false },
    { id: 'pension', name: 'Pension Scheme', cost: 3000, included: true },
    { id: 'gym', name: 'Gym Membership', cost: 800, included: true },
    { id: 'learning', name: 'Learning Budget', cost: 2000, included: false },
    { id: 'phone', name: 'Phone Allowance', cost: 600, included: false },
    { id: 'transport', name: 'Transport Allowance', cost: 1200, included: false },
    { id: 'childcare', name: 'Childcare Vouchers', cost: 1000, included: false },
  ];

  useEffect(() => {
    startAnimations();
    loadNegotiationData();
  }, []);

  const startAnimations = () => {
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 800,
        useNativeDriver: true,
      }),
      Animated.timing(slideAnim, {
        toValue: 0,
        duration: 600,
        useNativeDriver: true,
      }),
    ]).start();
  };

  const loadNegotiationData = async () => {
    try {
      // Try API first
      let loaded = false;
      try {
        const resp = await apiRequest(`negotiations?candidate_id=${candidate.id}`);
        if (resp.success && resp.data) {
          setNegotiationData(prevData => ({ ...prevData, ...resp.data }));
          await AsyncStorage.setItem(`negotiation_${candidate.id}`, JSON.stringify(resp.data));
          loaded = true;
        }
      } catch (apiErr) {
        const storedData = await AsyncStorage.getItem(`negotiation_${candidate.id}`);
        if (storedData) {
          const parsedData = JSON.parse(storedData);
          setNegotiationData(prevData => ({ ...prevData, ...parsedData }));
          loaded = true;
        }
      }
    } catch (error) {
      console.error('Error loading negotiation data:', error);
    }
  };

  const saveNegotiationData = async (updatedData) => {
    try {
      try { await apiRequest('negotiations', { method: 'PUT', body: { candidate_id: candidate.id, ...updatedData } }); } catch(e) { console.warn('API save failed:', e.message); }
      await AsyncStorage.setItem(`negotiation_${candidate.id}`, JSON.stringify(updatedData));
    } catch (error) {
      console.error('Error saving negotiation data:', error);
    }
  };

  const onRefresh = React.useCallback(async () => {
    setRefreshing(true);
    await loadNegotiationData();
    setRefreshing(false);
  }, []);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const calculateTotalPackage = (offer) => {
    return offer.baseSalary + offer.bonus + offer.stockOptions;
  };

  const calculateDifference = (current, original) => {
    const diff = current - original;
    const percentage = ((diff / original) * 100).toFixed(1);
    return { amount: diff, percentage };
  };

  const getNegotiationStatusColor = (status) => {
    switch (status) {
      case 'active': return '#F59E0B';
      case 'accepted': return '#10B981';
      case 'declined': return '#EF4444';
      case 'pending': return '#3B82F6';
      default: return '#6B7280';
    }
  };

  const getActorColor = (actor) => {
    return actor === 'company' ? '#8B5CF6' : '#10B981';
  };

  const handleSendCounterOffer = async () => {
    setIsLoading(true);
    
    try {
      // Simulate sending counter offer
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const updatedData = {
        ...negotiationData,
        currentRound: negotiationData.currentRound + 1,
        currentOffer: counterOffer,
        lastActivity: new Date().toISOString(),
        totalOffers: negotiationData.totalOffers + 1,
      };
      
      setNegotiationData(updatedData);
      await saveNegotiationData(updatedData);
      
      setShowCounterOfferModal(false);
      Alert.alert('Success', 'Counter offer has been sent successfully!');
      
    } catch (error) {
      console.error('Error sending counter offer:', error);
      Alert.alert('Error', 'Failed to send counter offer. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!messageData.message.trim()) return;
    
    setIsLoading(true);
    
    try {
      // Simulate sending message
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      setShowMessageModal(false);
      setMessageData({ type: 'email', subject: '', message: '', priority: 'normal' });
      Alert.alert('Success', 'Message has been sent successfully!');
      
    } catch (error) {
      console.error('Error sending message:', error);
      Alert.alert('Error', 'Failed to send message. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAcceptOffer = () => {
    Alert.alert(
      'Accept Current Offer',
      'Are you sure you want to accept the current offer? This will end the negotiation process.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Accept',
          style: 'default',
          onPress: async () => {
            const updatedData = {
              ...negotiationData,
              status: 'accepted',
              lastActivity: new Date().toISOString(),
            };
            setNegotiationData(updatedData);
            await saveNegotiationData(updatedData);
            Alert.alert('Success', 'Offer has been accepted!');
          },
        },
      ]
    );
  };

  const handleDeclineOffer = () => {
    Alert.alert(
      'Decline Offer',
      'Are you sure you want to decline this offer? This will end the negotiation process.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Decline',
          style: 'destructive',
          onPress: async () => {
            const updatedData = {
              ...negotiationData,
              status: 'declined',
              lastActivity: new Date().toISOString(),
            };
            setNegotiationData(updatedData);
            await saveNegotiationData(updatedData);
            Alert.alert('Declined', 'Offer has been declined.');
          },
        },
      ]
    );
  };

  const renderOverviewTab = () => (
    <View style={styles.tabContent}>
      {/* Negotiation Status */}
      <View style={styles.statusContainer}>
        <View style={styles.statusHeader}>
          <MaterialCommunityIcons name="handshake" size={24} color="#F59E0B" />
          <Text style={styles.statusTitle}>Negotiation Status</Text>
          <View style={[
            styles.statusBadge,
            { backgroundColor: getNegotiationStatusColor(negotiationData.status) }
          ]}>
            <Text style={styles.statusText}>
              {negotiationData.status.charAt(0).toUpperCase() + negotiationData.status.slice(1)}
            </Text>
          </View>
        </View>
      </View>

      {/* Candidate Summary */}
      <View style={styles.candidateContainer}>
        <View style={styles.candidateHeader}>
          <Image source={{ uri: (candidate.avatar && candidate.avatar.startsWith('http')) ? candidate.avatar : undefined }} style={styles.candidateAvatar} />
          <View style={styles.candidateInfo}>
            <Text style={styles.candidateName}>{candidate.name}</Text>
            <Text style={styles.candidatePosition}>{candidate.position}</Text>
            <Text style={styles.candidateCompany}>{candidate.department}</Text>
            <View style={styles.candidateContact}>
              <MaterialCommunityIcons name="email" size={14} color="#6B7280" />
              <Text style={styles.contactText}>{candidate.email}</Text>
            </View>
          </View>
        </View>
      </View>

      {/* Key Metrics */}
      <View style={styles.metricsContainer}>
        <Text style={styles.sectionTitle}>Negotiation Metrics</Text>
        
        <View style={styles.metricsGrid}>
          <View style={styles.metricCard}>
            <MaterialCommunityIcons name="repeat" size={24} color="#F59E0B" />
            <Text style={styles.metricValue}>{negotiationData.currentRound}</Text>
            <Text style={styles.metricLabel}>Current Round</Text>
          </View>
          
          <View style={styles.metricCard}>
            <MaterialCommunityIcons name="percent" size={24} color="#10B981" />
            <Text style={styles.metricValue}>{negotiationData.acceptanceProbability}%</Text>
            <Text style={styles.metricLabel}>Acceptance Probability</Text>
          </View>
          
          <View style={styles.metricCard}>
            <MaterialCommunityIcons name="calendar-clock" size={24} color="#3B82F6" />
            <Text style={styles.metricValue}>{negotiationData.timeToDecision}</Text>
            <Text style={styles.metricLabel}>Days to Decision</Text>
          </View>
          
          <View style={styles.metricCard}>
            <MaterialCommunityIcons name="chart-line" size={24} color="#8B5CF6" />
            <Text style={styles.metricValue}>{negotiationData.negotiationScore}</Text>
            <Text style={styles.metricLabel}>Negotiation Score</Text>
          </View>
        </View>
      </View>

      {/* Current Offer Summary */}
      <View style={styles.offerSummaryContainer}>
        <Text style={styles.sectionTitle}>Current Offer</Text>
        
        <View style={styles.offerSummaryCard}>
          <View style={styles.offerSummaryHeader}>
            <Text style={styles.totalPackageValue}>
              {formatCurrency(calculateTotalPackage(negotiationData.currentOffer))}
            </Text>
            <Text style={styles.totalPackageLabel}>Total Annual Package</Text>
          </View>
          
          <View style={styles.offerBreakdown}>
            <View style={styles.offerBreakdownItem}>
              <Text style={styles.offerBreakdownLabel}>Base Salary</Text>
              <Text style={styles.offerBreakdownValue}>
                {formatCurrency(negotiationData.currentOffer.baseSalary)}
              </Text>
            </View>
            
            <View style={styles.offerBreakdownItem}>
              <Text style={styles.offerBreakdownLabel}>Annual Bonus</Text>
              <Text style={styles.offerBreakdownValue}>
                {formatCurrency(negotiationData.currentOffer.bonus)}
              </Text>
            </View>
            
            <View style={styles.offerBreakdownItem}>
              <Text style={styles.offerBreakdownLabel}>Stock Options</Text>
              <Text style={styles.offerBreakdownValue}>
                {formatCurrency(negotiationData.currentOffer.stockOptions)}
              </Text>
            </View>
          </View>
        </View>
      </View>

      {/* Quick Actions */}
      <View style={styles.quickActionsContainer}>
        <Text style={styles.sectionTitle}>Quick Actions</Text>
        
        <View style={styles.quickActionsGrid}>
          <TouchableOpacity 
            style={styles.quickActionButton}
            onPress={() => setShowCounterOfferModal(true)}
          >
            <MaterialCommunityIcons name="reply" size={20} color="#F59E0B" />
            <Text style={styles.quickActionText}>Counter Offer</Text>
          </TouchableOpacity>
          
          <TouchableOpacity 
            style={styles.quickActionButton}
            onPress={() => setShowMessageModal(true)}
          >
            <MaterialCommunityIcons name="message" size={20} color="#3B82F6" />
            <Text style={styles.quickActionText}>Send Message</Text>
          </TouchableOpacity>
          
          <TouchableOpacity 
            style={styles.quickActionButton}
            onPress={handleAcceptOffer}
          >
            <MaterialCommunityIcons name="check" size={20} color="#10B981" />
            <Text style={styles.quickActionText}>Accept Offer</Text>
          </TouchableOpacity>
          
          <TouchableOpacity 
            style={styles.quickActionButton}
            onPress={handleDeclineOffer}
          >
            <MaterialCommunityIcons name="close" size={20} color="#EF4444" />
            <Text style={styles.quickActionText}>Decline</Text>
          </TouchableOpacity>
        </View>
      </View>

      {/* Recent Activity */}
      <View style={styles.recentActivityContainer}>
        <Text style={styles.sectionTitle}>Recent Activity</Text>
        
        <View style={styles.activityList}>
          {negotiationHistory.slice(0, 3).map((item) => (
            <View key={item.id} style={styles.activityItem}>
              <View style={[
                styles.activityIndicator,
                { backgroundColor: getActorColor(item.actor) }
              ]} />
              <View style={styles.activityContent}>
                <Text style={styles.activityTitle}>{item.title}</Text>
                <Text style={styles.activityDetails}>{item.details}</Text>
                <Text style={styles.activityTime}>{formatDate(item.date)}</Text>
              </View>
            </View>
          ))}
        </View>
        
        <TouchableOpacity 
          style={styles.viewAllButton}
          onPress={() => setShowHistoryModal(true)}
        >
          <Text style={styles.viewAllText}>View Full History</Text>
          <MaterialCommunityIcons name="arrow-right" size={16} color="#8B5CF6" />
        </TouchableOpacity>
      </View>
    </View>
  );

  const renderComparisonTab = () => (
    <View style={styles.tabContent}>
      <Text style={styles.sectionTitle}>Offer Comparison</Text>
      
      {/* Comparison Table */}
      <View style={styles.comparisonContainer}>
        <View style={styles.comparisonHeader}>
          <Text style={styles.comparisonHeaderText}>Component</Text>
          <Text style={styles.comparisonHeaderText}>Original</Text>
          <Text style={styles.comparisonHeaderText}>Current</Text>
          <Text style={styles.comparisonHeaderText}>Requested</Text>
        </View>
        
        {/* Base Salary Comparison */}
        <View style={styles.comparisonRow}>
          <Text style={styles.comparisonLabel}>Base Salary</Text>
          <Text style={styles.comparisonValue}>
            {formatCurrency(negotiationData.originalOffer.baseSalary)}
          </Text>
          <Text style={styles.comparisonValue}>
            {formatCurrency(negotiationData.currentOffer.baseSalary)}
          </Text>
          <Text style={styles.comparisonValue}>
            {formatCurrency(negotiationData.candidateRequests.baseSalary)}
          </Text>
        </View>
        
        {/* Bonus Comparison */}
        <View style={styles.comparisonRow}>
          <Text style={styles.comparisonLabel}>Annual Bonus</Text>
          <Text style={styles.comparisonValue}>
            {formatCurrency(negotiationData.originalOffer.bonus)}
          </Text>
          <Text style={styles.comparisonValue}>
            {formatCurrency(negotiationData.currentOffer.bonus)}
          </Text>
          <Text style={styles.comparisonValue}>
            {formatCurrency(negotiationData.candidateRequests.bonus)}
          </Text>
        </View>
        
        {/* Stock Options Comparison */}
        <View style={styles.comparisonRow}>
          <Text style={styles.comparisonLabel}>Stock Options</Text>
          <Text style={styles.comparisonValue}>
            {formatCurrency(negotiationData.originalOffer.stockOptions)}
          </Text>
          <Text style={styles.comparisonValue}>
            {formatCurrency(negotiationData.currentOffer.stockOptions)}
          </Text>
          <Text style={styles.comparisonValue}>
            {formatCurrency(negotiationData.candidateRequests.stockOptions)}
          </Text>
        </View>
        
        {/* Vacation Days Comparison */}
        <View style={styles.comparisonRow}>
          <Text style={styles.comparisonLabel}>Vacation Days</Text>
          <Text style={styles.comparisonValue}>
            {negotiationData.originalOffer.vacationDays} days
          </Text>
          <Text style={styles.comparisonValue}>
            {negotiationData.currentOffer.vacationDays} days
          </Text>
          <Text style={styles.comparisonValue}>
            {negotiationData.candidateRequests.vacationDays} days
          </Text>
        </View>
        
        {/* Work Location Comparison */}
        <View style={styles.comparisonRow}>
          <Text style={styles.comparisonLabel}>Work Location</Text>
          <Text style={styles.comparisonValue}>
            {negotiationData.originalOffer.workLocation}
          </Text>
          <Text style={styles.comparisonValue}>
            {negotiationData.currentOffer.workLocation}
          </Text>
          <Text style={styles.comparisonValue}>
            {negotiationData.candidateRequests.workLocation}
          </Text>
        </View>
        
        {/* Total Package Comparison */}
        <View style={[styles.comparisonRow, styles.comparisonTotalRow]}>
          <Text style={styles.comparisonTotalLabel}>Total Package</Text>
          <Text style={styles.comparisonTotalValue}>
            {formatCurrency(calculateTotalPackage(negotiationData.originalOffer))}
          </Text>
          <Text style={styles.comparisonTotalValue}>
            {formatCurrency(calculateTotalPackage(negotiationData.currentOffer))}
          </Text>
          <Text style={styles.comparisonTotalValue}>
            {formatCurrency(calculateTotalPackage(negotiationData.candidateRequests))}
          </Text>
        </View>
      </View>

      {/* Difference Analysis */}
      <View style={styles.differenceContainer}>
        <Text style={styles.sectionTitle}>Difference Analysis</Text>
        
        <View style={styles.differenceGrid}>
          <View style={styles.differenceCard}>
            <MaterialCommunityIcons name="trending-up" size={24} color="#10B981" />
            <Text style={styles.differenceValue}>
              {formatCurrency(calculateDifference(
                calculateTotalPackage(negotiationData.currentOffer),
                calculateTotalPackage(negotiationData.originalOffer)
              ).amount)}
            </Text>
            <Text style={styles.differenceLabel}>Increase from Original</Text>
            <Text style={styles.differencePercentage}>
              +{calculateDifference(
                calculateTotalPackage(negotiationData.currentOffer),
                calculateTotalPackage(negotiationData.originalOffer)
              ).percentage}%
            </Text>
          </View>
          
          <View style={styles.differenceCard}>
            <MaterialCommunityIcons name="target" size={24} color="#F59E0B" />
            <Text style={styles.differenceValue}>
              {formatCurrency(calculateDifference(
                calculateTotalPackage(negotiationData.candidateRequests),
                calculateTotalPackage(negotiationData.currentOffer)
              ).amount)}
            </Text>
            <Text style={styles.differenceLabel}>Gap to Requested</Text>
            <Text style={styles.differencePercentage}>
              +{calculateDifference(
                calculateTotalPackage(negotiationData.candidateRequests),
                calculateTotalPackage(negotiationData.currentOffer)
              ).percentage}%
            </Text>
          </View>
        </View>
      </View>

      {/* Benefits Comparison */}
      <View style={styles.benefitsComparisonContainer}>
        <Text style={styles.sectionTitle}>Benefits Comparison</Text>
        
        <View style={styles.benefitsGrid}>
          {availableBenefits.map((benefit) => (
            <View key={benefit.id} style={styles.benefitItem}>
              <View style={styles.benefitInfo}>
                <Text style={styles.benefitName}>{benefit.name}</Text>
                <Text style={styles.benefitCost}>Value: {formatCurrency(benefit.cost)}</Text>
              </View>
              <View style={styles.benefitStatus}>
                <MaterialCommunityIcons 
                  name={benefit.included ? 'check-circle' : 'circle-outline'} 
                  size={20} 
                  color={benefit.included ? '#10B981' : '#D1D5DB'} 
                />
              </View>
            </View>
          ))}
        </View>
      </View>
    </View>
  );

  const renderAnalyticsTab = () => (
    <View style={styles.tabContent}>
      <Text style={styles.sectionTitle}>Negotiation Analytics</Text>
      
      {/* Success Probability */}
      <View style={styles.analyticsContainer}>
        <Text style={styles.analyticsTitle}>Success Probability</Text>
        
        <View style={styles.probabilityContainer}>
          <View style={styles.probabilityCircle}>
            <Text style={styles.probabilityValue}>{negotiationData.acceptanceProbability}%</Text>
            <Text style={styles.probabilityLabel}>Acceptance</Text>
          </View>
          
          <View style={styles.probabilityFactors}>
            <View style={styles.probabilityFactor}>
              <MaterialCommunityIcons name="trending-up" size={16} color="#10B981" />
              <Text style={styles.probabilityFactorText}>Salary within range</Text>
            </View>
            <View style={styles.probabilityFactor}>
              <MaterialCommunityIcons name="check" size={16} color="#10B981" />
              <Text style={styles.probabilityFactorText}>Benefits competitive</Text>
            </View>
            <View style={styles.probabilityFactor}>
              <MaterialCommunityIcons name="alert" size={16} color="#F59E0B" />
              <Text style={styles.probabilityFactorText}>Work location compromise</Text>
            </View>
          </View>
        </View>
      </View>

      {/* Negotiation Timeline */}
      <View style={styles.timelineContainer}>
        <Text style={styles.analyticsTitle}>Negotiation Timeline</Text>
        
        <View style={styles.timelineChart}>
          <View style={styles.timelineAxis}>
            <Text style={styles.timelineLabel}>Week 1</Text>
            <Text style={styles.timelineLabel}>Week 2</Text>
            <Text style={styles.timelineLabel}>Week 3</Text>
            <Text style={styles.timelineLabel}>Week 4</Text>
          </View>
          
          <View style={styles.timelineData}>
            <View style={[styles.timelineBar, { height: 40, backgroundColor: '#8B5CF6' }]} />
            <View style={[styles.timelineBar, { height: 60, backgroundColor: '#10B981' }]} />
            <View style={[styles.timelineBar, { height: 80, backgroundColor: '#F59E0B' }]} />
            <View style={[styles.timelineBar, { height: 50, backgroundColor: '#3B82F6' }]} />
          </View>
        </View>
      </View>

      {/* Market Comparison */}
      <View style={styles.marketComparisonContainer}>
        <Text style={styles.analyticsTitle}>Market Comparison</Text>
        
        <View style={styles.marketStats}>
          <View style={styles.marketStat}>
            <MaterialCommunityIcons name="chart-box" size={24} color="#8B5CF6" />
            <Text style={styles.marketStatValue}>£78,500</Text>
            <Text style={styles.marketStatLabel}>Market Average</Text>
          </View>
          
          <View style={styles.marketStat}>
            <MaterialCommunityIcons name="trending-up" size={24} color="#10B981" />
            <Text style={styles.marketStatValue}>+4.3%</Text>
            <Text style={styles.marketStatLabel}>Above Market</Text>
          </View>
          
          <View style={styles.marketStat}>
            <MaterialCommunityIcons name="star" size={24} color="#F59E0B" />
            <Text style={styles.marketStatValue}>85th</Text>
            <Text style={styles.marketStatLabel}>Percentile</Text>
          </View>
        </View>
      </View>

      {/* Recommendation Engine */}
      <View style={styles.recommendationContainer}>
        <Text style={styles.analyticsTitle}>AI Recommendations</Text>
        
        <View style={styles.recommendationList}>
          <View style={styles.recommendationItem}>
            <MaterialCommunityIcons name="lightbulb" size={20} color="#F59E0B" />
            <View style={styles.recommendationContent}>
              <Text style={styles.recommendationTitle}>Consider Flexible Benefits</Text>
              <Text style={styles.recommendationDescription}>
                Offer learning budget instead of higher salary to reduce cost while adding value
              </Text>
            </View>
          </View>
          
          <View style={styles.recommendationItem}>
            <MaterialCommunityIcons name="clock" size={20} color="#3B82F6" />
            <View style={styles.recommendationContent}>
              <Text style={styles.recommendationTitle}>Time Pressure Strategy</Text>
              <Text style={styles.recommendationDescription}>
                Set a decision deadline to encourage faster acceptance
              </Text>
            </View>
          </View>
          
          <View style={styles.recommendationItem}>
            <MaterialCommunityIcons name="handshake" size={20} color="#10B981" />
            <View style={styles.recommendationContent}>
              <Text style={styles.recommendationTitle}>Compromise Opportunity</Text>
              <Text style={styles.recommendationDescription}>
                Meet halfway on vacation days to show flexibility
              </Text>
            </View>
          </View>
        </View>
      </View>
    </View>
  );

  const renderHistoryTab = () => (
    <View style={styles.tabContent}>
      <Text style={styles.sectionTitle}>Negotiation History</Text>
      
      <View style={styles.historyContainer}>
        {negotiationHistory.map((item) => (
          <View key={item.id} style={styles.historyItem}>
            <View style={styles.historyItemHeader}>
              <View style={[
                styles.historyIndicator,
                { backgroundColor: getActorColor(item.actor) }
              ]} />
              <View style={styles.historyItemInfo}>
                <Text style={styles.historyItemTitle}>{item.title}</Text>
                <Text style={styles.historyItemDate}>{formatDate(item.date)}</Text>
              </View>
              <View style={[
                styles.historyStatusBadge,
                { backgroundColor: item.status === 'active' ? '#F59E0B' : 
                                  item.status === 'sent' ? '#3B82F6' : '#10B981' }
              ]}>
                <Text style={styles.historyStatusText}>
                  {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                </Text>
              </View>
            </View>
            
            <Text style={styles.historyItemDetails}>{item.details}</Text>
            
            <View style={styles.historyItemActions}>
              <TouchableOpacity style={styles.historyActionButton}>
                <MaterialCommunityIcons name="eye" size={16} color="#8B5CF6" />
                <Text style={styles.historyActionText}>View Details</Text>
              </TouchableOpacity>
              
              {item.type === 'counter_offer' && (
                <TouchableOpacity style={styles.historyActionButton}>
                  <MaterialCommunityIcons name="reply" size={16} color="#10B981" />
                  <Text style={styles.historyActionText}>Respond</Text>
                </TouchableOpacity>
              )}
            </View>
          </View>
        ))}
      </View>
    </View>
  );

  const renderStrategyTab = () => (
    <View style={styles.tabContent}>
      <Text style={styles.sectionTitle}>Negotiation Strategy</Text>
      
      {/* Strategy Overview */}
      <View style={styles.strategyContainer}>
        <Text style={styles.strategyTitle}>Current Strategy</Text>
        
        <View style={styles.strategyCard}>
          <MaterialCommunityIcons name="strategy" size={24} color="#8B5CF6" />
          <View style={styles.strategyContent}>
            <Text style={styles.strategyName}>Collaborative Approach</Text>
            <Text style={styles.strategyDescription}>
              Focus on mutual benefits and long-term relationship building
            </Text>
          </View>
        </View>
      </View>

      {/* Key Tactics */}
      <View style={styles.tacticsContainer}>
        <Text style={styles.strategyTitle}>Key Tactics</Text>
        
        <View style={styles.tacticsList}>
          <View style={styles.tacticItem}>
            <MaterialCommunityIcons name="check-circle" size={20} color="#10B981" />
            <View style={styles.tacticContent}>
              <Text style={styles.tacticTitle}>Value-Based Negotiation</Text>
              <Text style={styles.tacticDescription}>
                Emphasize total compensation package rather than just salary
              </Text>
            </View>
          </View>
          
          <View style={styles.tacticItem}>
            <MaterialCommunityIcons name="clock" size={20} color="#F59E0B" />
            <View style={styles.tacticContent}>
              <Text style={styles.tacticTitle}>Strategic Timing</Text>
              <Text style={styles.tacticDescription}>
                Allow adequate time for consideration while maintaining momentum
              </Text>
            </View>
          </View>
          
          <View style={styles.tacticItem}>
            <MaterialCommunityIcons name="handshake" size={20} color="#3B82F6" />
            <View style={styles.tacticContent}>
              <Text style={styles.tacticTitle}>Win-Win Solutions</Text>
              <Text style={styles.tacticDescription}>
                Find creative solutions that benefit both parties
              </Text>
            </View>
          </View>
        </View>
      </View>

      {/* Risk Assessment */}
      <View style={styles.riskContainer}>
        <Text style={styles.strategyTitle}>Risk Assessment</Text>
        
        <View style={styles.riskList}>
          <View style={styles.riskItem}>
            <MaterialCommunityIcons name="alert" size={20} color="#EF4444" />
            <View style={styles.riskContent}>
              <Text style={styles.riskTitle}>High Risk</Text>
              <Text style={styles.riskDescription}>
                Candidate may accept competing offer if negotiation drags
              </Text>
            </View>
            <Text style={styles.riskProbability}>25%</Text>
          </View>
          
          <View style={styles.riskItem}>
            <MaterialCommunityIcons name="alert-circle" size={20} color="#F59E0B" />
            <View style={styles.riskContent}>
              <Text style={styles.riskTitle}>Medium Risk</Text>
              <Text style={styles.riskDescription}>
                Budget constraints may limit further salary increases
              </Text>
            </View>
            <Text style={styles.riskProbability}>45%</Text>
          </View>
          
          <View style={styles.riskItem}>
            <MaterialCommunityIcons name="information" size={20} color="#3B82F6" />
            <View style={styles.riskContent}>
              <Text style={styles.riskTitle}>Low Risk</Text>
              <Text style={styles.riskDescription}>
                Strong candidate interest in company culture and role
              </Text>
            </View>
            <Text style={styles.riskProbability}>15%</Text>
          </View>
        </View>
      </View>

      {/* Next Steps */}
      <View style={styles.nextStepsContainer}>
        <Text style={styles.strategyTitle}>Recommended Next Steps</Text>
        
        <View style={styles.nextStepsList}>
          <View style={styles.nextStepItem}>
            <View style={styles.nextStepNumber}>
              <Text style={styles.nextStepNumberText}>1</Text>
            </View>
            <View style={styles.nextStepContent}>
              <Text style={styles.nextStepTitle}>Schedule Call</Text>
              <Text style={styles.nextStepDescription}>
                Arrange a phone call to discuss remaining concerns
              </Text>
            </View>
          </View>
          
          <View style={styles.nextStepItem}>
            <View style={styles.nextStepNumber}>
              <Text style={styles.nextStepNumberText}>2</Text>
            </View>
            <View style={styles.nextStepContent}>
              <Text style={styles.nextStepTitle}>Present Final Offer</Text>
              <Text style={styles.nextStepDescription}>
                Make a compelling final offer with clear deadline
              </Text>
            </View>
          </View>
          
          <View style={styles.nextStepItem}>
            <View style={styles.nextStepNumber}>
              <Text style={styles.nextStepNumberText}>3</Text>
            </View>
            <View style={styles.nextStepContent}>
              <Text style={styles.nextStepTitle}>Follow Up</Text>
              <Text style={styles.nextStepDescription}>
                Send follow-up email summarizing key points
              </Text>
            </View>
          </View>
        </View>
      </View>
    </View>
  );

  const renderCommunicationTab = () => (
    <View style={styles.tabContent}>
      <Text style={styles.sectionTitle}>Communication History</Text>
      
      {/* Message Thread */}
      <View style={styles.messageThreadContainer}>
        <View style={styles.messageItem}>
          <View style={styles.messageHeader}>
            <MaterialCommunityIcons name="account-circle" size={24} color="#8B5CF6" />
            <View style={styles.messageInfo}>
              <Text style={styles.messageSender}>HR Team</Text>
              <Text style={styles.messageTime}>2 hours ago</Text>
            </View>
          </View>
          <Text style={styles.messageContent}>
            Thank you for your counter-proposal. We've reviewed your requests and are pleased to offer an updated package. Please find the revised terms attached.
          </Text>
        </View>
        
        <View style={styles.messageItem}>
          <View style={styles.messageHeader}>
            <MaterialCommunityIcons name="account" size={24} color="#10B981" />
            <View style={styles.messageInfo}>
              <Text style={styles.messageSender}>{candidate.name}</Text>
              <Text style={styles.messageTime}>1 day ago</Text>
            </View>
          </View>
          <Text style={styles.messageContent}>
            I appreciate the offer and am very interested in joining the team. I'd like to discuss a few adjustments to better align with my expectations, particularly regarding remote work flexibility and vacation time.
          </Text>
        </View>
        
        <View style={styles.messageItem}>
          <View style={styles.messageHeader}>
            <MaterialCommunityIcons name="account-circle" size={24} color="#8B5CF6" />
            <View style={styles.messageInfo}>
              <Text style={styles.messageSender}>HR Team</Text>
              <Text style={styles.messageTime}>3 days ago</Text>
            </View>
          </View>
          <Text style={styles.messageContent}>
            We're excited about the possibility of you joining our team. Based on our interview discussions, we've prepared an initial offer that we believe reflects your experience and the value you'll bring to our organization.
          </Text>
        </View>
      </View>

      {/* Quick Reply Templates */}
      <View style={styles.quickRepliesContainer}>
        <Text style={styles.quickRepliesTitle}>Quick Reply Templates</Text>
        
        <View style={styles.quickRepliesList}>
          <TouchableOpacity style={styles.quickReplyButton}>
            <Text style={styles.quickReplyText}>Request more time to consider</Text>
          </TouchableOpacity>
          
          <TouchableOpacity style={styles.quickReplyButton}>
            <Text style={styles.quickReplyText}>Schedule a call to discuss</Text>
          </TouchableOpacity>
          
          <TouchableOpacity style={styles.quickReplyButton}>
            <Text style={styles.quickReplyText}>Ask for clarification on benefits</Text>
          </TouchableOpacity>
          
          <TouchableOpacity style={styles.quickReplyButton}>
            <Text style={styles.quickReplyText}>Express enthusiasm and acceptance</Text>
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar barStyle="light-content" backgroundColor="#F59E0B" />
      
      {/* Header with gradient - matching JobOfferDetailsScreen style */}
      <LinearGradient colors={['#F59E0B', '#D97706']} style={styles.header}>
        <View style={styles.headerContent}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Ionicons name="arrow-back" size={24} color="#FFFFFF" />
          </TouchableOpacity>
          
          <View style={styles.headerInfo}>
            <Text style={styles.headerTitle}>Negotiation Center</Text>
            <Text style={styles.headerSubtitle}>{candidate.name}</Text>
          </View>
          
          <TouchableOpacity
            style={styles.headerAction}
            onPress={() => setShowAnalyticsModal(true)}
          >
            <MaterialCommunityIcons name="chart-line" size={24} color="#FFFFFF" />
          </TouchableOpacity>
        </View>
      </LinearGradient>

      {/* Tab Navigation */}
      <View style={styles.tabContainer}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.tabScroll}>
          {tabs.map((tab) => (
            <TouchableOpacity
              key={tab.id}
              style={[
                styles.tab,
                activeTab === tab.id && styles.activeTab
              ]}
              onPress={() => setActiveTab(tab.id)}
            >
              <MaterialCommunityIcons
                name={tab.icon}
                size={16}
                color={activeTab === tab.id ? '#F59E0B' : '#6B7280'}
              />
              <Text style={[
                styles.tabText,
                activeTab === tab.id && styles.activeTabText
              ]}>
                {tab.name}
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>

      <Animated.View 
        style={[
          styles.content,
          {
            opacity: fadeAnim,
            transform: [{ translateY: slideAnim }]
          }
        ]}
      >
        <ScrollView 
          showsVerticalScrollIndicator={false} 
          contentContainerStyle={styles.scrollContent}
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
          }
        >
          {activeTab === 'overview' && renderOverviewTab()}
          {activeTab === 'comparison' && renderComparisonTab()}
          {activeTab === 'analytics' && renderAnalyticsTab()}
          {activeTab === 'history' && renderHistoryTab()}
          {activeTab === 'strategy' && renderStrategyTab()}
          {activeTab === 'communication' && renderCommunicationTab()}
        </ScrollView>
      </Animated.View>

      {/* Counter Offer Modal */}
      <Modal
        visible={showCounterOfferModal}
        animationType="slide"
        presentationStyle="pageSheet"
        onRequestClose={() => setShowCounterOfferModal(false)}
      >
        <SafeAreaView style={styles.modalContainer}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>Send Counter Offer</Text>
            <TouchableOpacity onPress={() => setShowCounterOfferModal(false)}>
              <MaterialCommunityIcons name="close" size={24} color="#6B7280" />
            </TouchableOpacity>
          </View>
          
          <ScrollView style={styles.modalContent}>
            <View style={styles.counterOfferForm}>
              <View style={styles.formSection}>
                <Text style={styles.formSectionTitle}>Compensation</Text>
                
                <View style={styles.formField}>
                  <Text style={styles.formLabel}>Base Salary</Text>
                  <TextInput
                    style={styles.formInput}
                    value={counterOffer.baseSalary.toString()}
                    onChangeText={(text) => setCounterOffer(prev => ({ 
                      ...prev, 
                      baseSalary: parseInt(text.replace(/[^0-9]/g, '')) || 0 
                    }))}
                    placeholder="Enter base salary"
                    keyboardType="numeric"
                  />
                </View>
                
                <View style={styles.formField}>
                  <Text style={styles.formLabel}>Annual Bonus</Text>
                  <TextInput
                    style={styles.formInput}
                    value={counterOffer.bonus.toString()}
                    onChangeText={(text) => setCounterOffer(prev => ({ 
                      ...prev, 
                      bonus: parseInt(text.replace(/[^0-9]/g, '')) || 0 
                    }))}
                    placeholder="Enter annual bonus"
                    keyboardType="numeric"
                  />
                </View>
                
                <View style={styles.formField}>
                  <Text style={styles.formLabel}>Stock Options</Text>
                  <TextInput
                    style={styles.formInput}
                    value={counterOffer.stockOptions.toString()}
                    onChangeText={(text) => setCounterOffer(prev => ({ 
                      ...prev, 
                      stockOptions: parseInt(text.replace(/[^0-9]/g, '')) || 0 
                    }))}
                    placeholder="Enter stock options value"
                    keyboardType="numeric"
                  />
                </View>
              </View>
              
              <View style={styles.formSection}>
                <Text style={styles.formSectionTitle}>Work Conditions</Text>
                
                <View style={styles.formField}>
                  <Text style={styles.formLabel}>Vacation Days</Text>
                  <TextInput
                    style={styles.formInput}
                    value={counterOffer.vacationDays.toString()}
                    onChangeText={(text) => setCounterOffer(prev => ({ 
                      ...prev, 
                      vacationDays: parseInt(text) || 0 
                    }))}
                    placeholder="Enter vacation days"
                    keyboardType="numeric"
                  />
                </View>
                
                <View style={styles.formField}>
                  <Text style={styles.formLabel}>Work Location</Text>
                  <TextInput
                    style={styles.formInput}
                    value={counterOffer.workLocation}
                    onChangeText={(text) => setCounterOffer(prev => ({ 
                      ...prev, 
                      workLocation: text 
                    }))}
                    placeholder="e.g., Remote, Hybrid, Office"
                  />
                </View>
              </View>
              
              <View style={styles.formSection}>
                <Text style={styles.formSectionTitle}>Message</Text>
                
                <TextInput
                  style={styles.formTextArea}
                  value={counterOffer.message}
                  onChangeText={(text) => setCounterOffer(prev => ({ 
                    ...prev, 
                    message: text 
                  }))}
                  placeholder="Add a personal message to explain your counter offer..."
                  multiline
                  numberOfLines={4}
                />
              </View>
              
              <View style={styles.totalPackagePreview}>
                <Text style={styles.totalPackageTitle}>Total Package Preview</Text>
                <Text style={styles.totalPackageValue}>
                  {formatCurrency(counterOffer.baseSalary + counterOffer.bonus + counterOffer.stockOptions)}
                </Text>
              </View>
            </View>
          </ScrollView>
          
          <View style={styles.modalActions}>
            <TouchableOpacity
              style={styles.modalCancelButton}
              onPress={() => setShowCounterOfferModal(false)}
            >
              <Text style={styles.modalCancelText}>Cancel</Text>
            </TouchableOpacity>
            
            <TouchableOpacity
              style={styles.modalSendButton}
              onPress={handleSendCounterOffer}
              disabled={isLoading}
            >
              {isLoading ? (
                <ActivityIndicator color="#FFFFFF" size={20} />
              ) : (
                <>
                  <MaterialCommunityIcons name="send" size={16} color="#FFFFFF" />
                  <Text style={styles.modalSendText}>Send Counter Offer</Text>
                </>
              )}
            </TouchableOpacity>
          </View>
        </SafeAreaView>
      </Modal>

      {/* Message Modal */}
      <Modal
        visible={showMessageModal}
        animationType="slide"
        presentationStyle="pageSheet"
        onRequestClose={() => setShowMessageModal(false)}
      >
        <SafeAreaView style={styles.modalContainer}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>Send Message</Text>
            <TouchableOpacity onPress={() => setShowMessageModal(false)}>
              <MaterialCommunityIcons name="close" size={24} color="#6B7280" />
            </TouchableOpacity>
          </View>
          
          <ScrollView style={styles.modalContent}>
            <View style={styles.messageForm}>
              <View style={styles.formField}>
                <Text style={styles.formLabel}>Message Type</Text>
                <View style={styles.messageTypeButtons}>
                  {['email', 'phone', 'meeting'].map((type) => (
                    <TouchableOpacity
                      key={type}
                      style={[
                        styles.messageTypeButton,
                        messageData.type === type && styles.activeMessageTypeButton
                      ]}
                      onPress={() => setMessageData(prev => ({ ...prev, type }))}
                    >
                      <MaterialCommunityIcons 
                        name={type === 'email' ? 'email' : type === 'phone' ? 'phone' : 'calendar'} 
                        size={16} 
                        color={messageData.type === type ? '#FFFFFF' : '#6B7280'} 
                      />
                      <Text style={[
                        styles.messageTypeButtonText,
                        messageData.type === type && styles.activeMessageTypeButtonText
                      ]}>
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>
              
              <View style={styles.formField}>
                <Text style={styles.formLabel}>Subject</Text>
                <TextInput
                  style={styles.formInput}
                  value={messageData.subject}
                  onChangeText={(text) => setMessageData(prev => ({ ...prev, subject: text }))}
                  placeholder="Enter message subject"
                />
              </View>
              
              <View style={styles.formField}>
                <Text style={styles.formLabel}>Message</Text>
                <TextInput
                  style={styles.formTextArea}
                  value={messageData.message}
                  onChangeText={(text) => setMessageData(prev => ({ ...prev, message: text }))}
                  placeholder="Type your message here..."
                  multiline
                  numberOfLines={6}
                />
              </View>
              
              <View style={styles.formField}>
                <Text style={styles.formLabel}>Priority</Text>
                <View style={styles.priorityButtons}>
                  {['low', 'normal', 'high'].map((priority) => (
                    <TouchableOpacity
                      key={priority}
                      style={[
                        styles.priorityButton,
                        messageData.priority === priority && styles.activePriorityButton
                      ]}
                      onPress={() => setMessageData(prev => ({ ...prev, priority }))}
                    >
                      <Text style={[
                        styles.priorityButtonText,
                        messageData.priority === priority && styles.activePriorityButtonText
                      ]}>
                        {priority.charAt(0).toUpperCase() + priority.slice(1)}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>
            </View>
          </ScrollView>
          
          <View style={styles.modalActions}>
            <TouchableOpacity
              style={styles.modalCancelButton}
              onPress={() => setShowMessageModal(false)}
            >
              <Text style={styles.modalCancelText}>Cancel</Text>
            </TouchableOpacity>
            
            <TouchableOpacity
              style={styles.modalSendButton}
              onPress={handleSendMessage}
              disabled={isLoading || !messageData.message.trim()}
            >
              {isLoading ? (
                <ActivityIndicator color="#FFFFFF" size={20} />
              ) : (
                <>
                  <MaterialCommunityIcons name="send" size={16} color="#FFFFFF" />
                  <Text style={styles.modalSendText}>Send Message</Text>
                </>
              )}
            </TouchableOpacity>
          </View>
        </SafeAreaView>
      </Modal>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F3F4F6',
  },
  header: {
    paddingTop: 10,
    paddingBottom: 20,
    paddingHorizontal: 20,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backButton: {
    padding: 8,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
  },
  headerInfo: {
    flex: 1,
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: 'white',
    textAlign: 'center',
  },
  headerSubtitle: {
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.8)',
    textAlign: 'center',
    marginTop: 2,
  },
  headerAction: {
    padding: 8,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
  },
  tabContainer: {
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  tabScroll: {
    paddingHorizontal: 16,
  },
  tab: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    marginRight: 8,
    borderRadius: 8,
  },
  activeTab: {
    backgroundColor: '#FEF3C7',
  },
  tabText: {
    marginLeft: 6,
    fontSize: 14,
    fontWeight: '500',
    color: '#6B7280',
  },
  activeTabText: {
    color: '#F59E0B',
    fontWeight: '600',
  },
  content: {
    flex: 1,
    backgroundColor: '#F3F4F6',
  },
  scrollContent: {
    padding: 16,
    paddingBottom: 32,
  },
  tabContent: {
    gap: 20,
  },
  statusContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  statusHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  statusTitle: {
    flex: 1,
    marginLeft: 8,
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1F2937',
  },
  statusBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 12,
  },
  statusText: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#FFFFFF',
  },
  candidateContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  candidateHeader: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  candidateAvatar: {
    width: 60,
    height: 60,
    borderRadius: 30,
    marginRight: 16,
  },
  candidateInfo: {
    flex: 1,
  },
  candidateName: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#1F2937',
    marginBottom: 4,
  },
  candidatePosition: {
    fontSize: 16,
    color: '#6B7280',
    marginBottom: 2,
  },
  candidateCompany: {
    fontSize: 14,
    color: '#F59E0B',
    fontWeight: '500',
    marginBottom: 8,
  },
  candidateContact: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  contactText: {
    marginLeft: 6,
    fontSize: 12,
    color: '#6B7280',
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1F2937',
    marginBottom: 16,
  },
  metricsContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  metricsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  metricCard: {
    width: '48%',
    alignItems: 'center',
    backgroundColor: '#F9FAFB',
    padding: 16,
    borderRadius: 12,
    marginBottom: 12,
  },
  metricValue: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#1F2937',
    marginTop: 8,
    marginBottom: 4,
  },
  metricLabel: {
    fontSize: 12,
    color: '#6B7280',
    textAlign: 'center',
  },
  offerSummaryContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  offerSummaryCard: {
    backgroundColor: '#F9FAFB',
    borderRadius: 12,
    padding: 16,
  },
  offerSummaryHeader: {
    alignItems: 'center',
    marginBottom: 16,
  },
  totalPackageValue: {
    fontSize: 32,
    fontWeight: 'bold',
    color: '#1F2937',
    marginBottom: 4,
  },
  totalPackageLabel: {
    fontSize: 14,
    color: '#6B7280',
  },
  offerBreakdown: {
    gap: 8,
  },
  offerBreakdownItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  offerBreakdownLabel: {
    fontSize: 14,
    color: '#6B7280',
  },
  offerBreakdownValue: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
  },
  quickActionsContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  quickActionsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  quickActionButton: {
    width: '48%',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#F9FAFB',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  quickActionText: {
    marginLeft: 6,
    fontSize: 12,
    fontWeight: '600',
    color: '#6B7280',
  },
  recentActivityContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  activityList: {
    gap: 16,
  },
  activityItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  activityIndicator: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginTop: 6,
    marginRight: 12,
  },
  activityContent: {
    flex: 1,
  },
  activityTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 4,
  },
  activityDetails: {
    fontSize: 12,
    color: '#6B7280',
    marginBottom: 4,
  },
  activityTime: {
    fontSize: 11,
    color: '#9CA3AF',
  },
  viewAllButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 16,
    paddingVertical: 8,
  },
  viewAllText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#8B5CF6',
    marginRight: 4,
  },
  comparisonContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  comparisonHeader: {
    flexDirection: 'row',
    backgroundColor: '#F9FAFB',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    marginBottom: 8,
  },
  comparisonHeaderText: {
    flex: 1,
    fontSize: 12,
    fontWeight: 'bold',
    color: '#6B7280',
    textAlign: 'center',
  },
  comparisonRow: {
    flexDirection: 'row',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#F3F4F6',
  },
  comparisonTotalRow: {
    backgroundColor: '#FEF3C7',
    borderRadius: 8,
    marginTop: 8,
    borderBottomWidth: 0,
  },
  comparisonLabel: {
    flex: 1,
    fontSize: 14,
    color: '#1F2937',
    fontWeight: '500',
  },
  comparisonTotalLabel: {
    flex: 1,
    fontSize: 14,
    color: '#1F2937',
    fontWeight: 'bold',
  },
  comparisonValue: {
    flex: 1,
    fontSize: 12,
    color: '#6B7280',
    textAlign: 'center',
  },
  comparisonTotalValue: {
    flex: 1,
    fontSize: 12,
    color: '#1F2937',
    fontWeight: 'bold',
    textAlign: 'center',
  },
  differenceContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  differenceGrid: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  differenceCard: {
    width: '48%',
    alignItems: 'center',
    backgroundColor: '#F9FAFB',
    padding: 16,
    borderRadius: 12,
  },
  differenceValue: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1F2937',
    marginTop: 8,
    marginBottom: 4,
  },
  differenceLabel: {
    fontSize: 12,
    color: '#6B7280',
    textAlign: 'center',
    marginBottom: 4,
  },
  differencePercentage: {
    fontSize: 12,
    fontWeight: '600',
    color: '#10B981',
  },
  benefitsComparisonContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  benefitsGrid: {
    gap: 12,
  },
  benefitItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#F9FAFB',
    padding: 12,
    borderRadius: 8,
  },
  benefitInfo: {
    flex: 1,
  },
  benefitName: {
    fontSize: 14,
    fontWeight: '500',
    color: '#1F2937',
    marginBottom: 2,
  },
  benefitCost: {
    fontSize: 12,
    color: '#6B7280',
  },
  benefitStatus: {
    marginLeft: 12,
  },
  analyticsContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 20,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  analyticsTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#1F2937',
    marginBottom: 16,
  },
  probabilityContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  probabilityCircle: {
    width: 100,
    height: 100,
    borderRadius: 50,
    backgroundColor: '#10B981',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 20,
  },
  probabilityValue: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#FFFFFF',
  },
  probabilityLabel: {
    fontSize: 12,
    color: '#FFFFFF',
  },
  probabilityFactors: {
    flex: 1,
    gap: 8,
  },
  probabilityFactor: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  probabilityFactorText: {
    marginLeft: 8,
    fontSize: 12,
    color: '#6B7280',
  },
  timelineContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 20,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  timelineChart: {
    height: 120,
  },
  timelineAxis: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  timelineLabel: {
    fontSize: 12,
    color: '#6B7280',
  },
  timelineData: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    height: 80,
  },
  timelineBar: {
    width: 40,
    borderRadius: 4,
  },
  marketComparisonContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 20,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  marketStats: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  marketStat: {
    alignItems: 'center',
    flex: 1,
  },
  marketStatValue: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1F2937',
    marginTop: 8,
    marginBottom: 4,
  },
  marketStatLabel: {
    fontSize: 12,
    color: '#6B7280',
    textAlign: 'center',
  },
  recommendationContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  recommendationList: {
    gap: 16,
  },
  recommendationItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  recommendationContent: {
    marginLeft: 12,
    flex: 1,
  },
  recommendationTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 4,
  },
  recommendationDescription: {
    fontSize: 12,
    color: '#6B7280',
    lineHeight: 16,
  },
  historyContainer: {
    gap: 16,
  },
  historyItem: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 1,
    },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  historyItemHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  historyIndicator: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginRight: 12,
  },
  historyItemInfo: {
    flex: 1,
  },
  historyItemTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 2,
  },
  historyItemDate: {
    fontSize: 12,
    color: '#6B7280',
  },
  historyStatusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
  },
  historyStatusText: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#FFFFFF',
  },
  historyItemDetails: {
    fontSize: 12,
    color: '#6B7280',
    marginBottom: 12,
    lineHeight: 16,
  },
  historyItemActions: {
    flexDirection: 'row',
    gap: 12,
  },
  historyActionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F9FAFB',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
  },
  historyActionText: {
    marginLeft: 4,
    fontSize: 12,
    color: '#6B7280',
  },
  strategyContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 20,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  strategyTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#1F2937',
    marginBottom: 16,
  },
  strategyCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F9FAFB',
    padding: 16,
    borderRadius: 12,
  },
  strategyContent: {
    marginLeft: 12,
    flex: 1,
  },
  strategyName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 4,
  },
  strategyDescription: {
    fontSize: 12,
    color: '#6B7280',
    lineHeight: 16,
  },
  tacticsContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 20,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  tacticsList: {
    gap: 16,
  },
  tacticItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  tacticContent: {
    marginLeft: 12,
    flex: 1,
  },
  tacticTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 4,
  },
  tacticDescription: {
    fontSize: 12,
    color: '#6B7280',
    lineHeight: 16,
  },
  riskContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 20,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  riskList: {
    gap: 16,
  },
  riskItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  riskContent: {
    marginLeft: 12,
    flex: 1,
  },
  riskTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 4,
  },
  riskDescription: {
    fontSize: 12,
    color: '#6B7280',
    lineHeight: 16,
  },
  riskProbability: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#F59E0B',
    marginLeft: 8,
  },
  nextStepsContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  nextStepsList: {
    gap: 16,
  },
  nextStepItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  nextStepNumber: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: '#F59E0B',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  nextStepNumberText: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#FFFFFF',
  },
  nextStepContent: {
    flex: 1,
  },
  nextStepTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 4,
  },
  nextStepDescription: {
    fontSize: 12,
    color: '#6B7280',
    lineHeight: 16,
  },
  messageThreadContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 20,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  messageItem: {
    marginBottom: 20,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#F3F4F6',
  },
  messageHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  messageInfo: {
    marginLeft: 8,
  },
  messageSender: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
  },
  messageTime: {
    fontSize: 12,
    color: '#6B7280',
  },
  messageContent: {
    fontSize: 14,
    color: '#374151',
    lineHeight: 20,
  },
  quickRepliesContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  quickRepliesTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#1F2937',
    marginBottom: 16,
  },
  quickRepliesList: {
    gap: 8,
  },
  quickReplyButton: {
    backgroundColor: '#F9FAFB',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  quickReplyText: {
    fontSize: 14,
    color: '#6B7280',
  },
  modalContainer: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1F2937',
  },
  modalContent: {
    flex: 1,
    padding: 20,
  },
  counterOfferForm: {
    gap: 24,
  },
  formSection: {
    gap: 16,
  },
  formSectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#1F2937',
  },
  formField: {
    gap: 8,
  },
  formLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: '#374151',
  },
  formInput: {
    borderWidth: 1,
    borderColor: '#D1D5DB',
    borderRadius: 8,
    padding: 12,
    fontSize: 14,
    color: '#1F2937',
    backgroundColor: '#FFFFFF',
  },
  formTextArea: {
    borderWidth: 1,
    borderColor: '#D1D5DB',
    borderRadius: 8,
    padding: 12,
    fontSize: 14,
    color: '#1F2937',
    backgroundColor: '#FFFFFF',
    minHeight: 80,
    textAlignVertical: 'top',
  },
  totalPackagePreview: {
    backgroundColor: '#F9FAFB',
    padding: 16,
    borderRadius: 12,
    alignItems: 'center',
  },
  totalPackageTitle: {
    fontSize: 14,
    color: '#6B7280',
    marginBottom: 4,
  },
  totalPackageValue: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#1F2937',
  },
  modalActions: {
    flexDirection: 'row',
    padding: 20,
    gap: 12,
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
  },
  modalCancelButton: {
    flex: 1,
    paddingVertical: 12,
    alignItems: 'center',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#D1D5DB',
  },
  modalCancelText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#6B7280',
  },
  modalSendButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#F59E0B',
    paddingVertical: 12,
    borderRadius: 8,
  },
  modalSendText: {
    marginLeft: 6,
    fontSize: 14,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  messageForm: {
    gap: 20,
  },
  messageTypeButtons: {
    flexDirection: 'row',
    gap: 8,
  },
  messageTypeButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#D1D5DB',
    backgroundColor: '#FFFFFF',
  },
  activeMessageTypeButton: {
    backgroundColor: '#F59E0B',
    borderColor: '#F59E0B',
  },
  messageTypeButtonText: {
    marginLeft: 6,
    fontSize: 12,
    color: '#6B7280',
  },
  activeMessageTypeButtonText: {
    color: '#FFFFFF',
  },
  priorityButtons: {
    flexDirection: 'row',
    gap: 8,
  },
  priorityButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#D1D5DB',
    backgroundColor: '#FFFFFF',
  },
  activePriorityButton: {
    backgroundColor: '#F59E0B',
    borderColor: '#F59E0B',
  },
  priorityButtonText: {
    fontSize: 12,
    color: '#6B7280',
  },
  activePriorityButtonText: {
    color: '#FFFFFF',
  },
});
